const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

class FilterDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", (e) => {
      if (!this.summary.contains(e.target)) return;

      e.preventDefault();

      if (this._allowToggle) {
        this.details.open ? this.close() : this.open();
      }
    });

    this.lastAllowToggle = null;

    const toggleOpen = () => {
      if (!this._allowToggle) {
        this._forceOpen();
        this.summary.tabIndex = -1;
      } else if (this.lastAllowToggle === false && this._allowToggle) {
        this._forceClosed();
        this.summary.removeAttribute("tabindex");
      }

      this.lastAllowToggle = this._allowToggle;
    };

    toggleOpen();

    this.onResize = toggleOpen;

    window.addEventListener("resize", this.onResize);

    this.addEventListener("click", (e) => {
      if (!e.target.closest("[data-close]")) return;

      if (this._allowToggle && e.target.closest("filter-drawer") === this) {
        this.close();
      }
    });

    this.addEventListener("filter-drawer:force-closed", () => {
      if (this._allowToggle) {
        this._forceClosed();
      }
    });

    this.addEventListener("filter-drawer:close", () => {
      if (this._allowToggle) {
        this.close();
      }
    });

    this.handleChange = () => {
      if (this._allowToggle && this._closeOnChange) {
        // Brief delay so user can register their change before drawer closes
        setTimeout(() => {
          this.close();
        }, 250);
      }
    };

    this.addEventListener("change", this.handleChange);
    this.addEventListener("aria-listbox:change", this.handleChange);

    // Modal only: the desktop panel sits in flow, so closing it on an outside
    // click would shift the page under the pointer.
    this.handleDocumentClick = (e) => {
      const trigger = e.target.closest("[data-filter-drawer-toggle]");
      if (trigger && this.triggers.includes(trigger)) {
        this.details.open ? this.close() : this.open();
        return;
      }

      // composedPath() is fixed when the event is dispatched, so a click whose
      // target was removed mid-dispatch (e.g. a drawer filter chip rebuilding
      // its list) still counts as inside; this.contains(e.target) would not.
      if (
        this._isModal &&
        this.details.open &&
        this._allowToggle &&
        !e.composedPath().includes(this)
      ) {
        this.close();
      }
    };

    this.handleKeyUp = (e) => {
      if (
        this.details.open &&
        this._allowToggle &&
        e.code.toUpperCase() === "ESCAPE"
      ) {
        this.close();
      }
    };

    document.addEventListener("keyup", this.handleKeyUp);
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keyup", this.handleKeyUp);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  _forceOpen() {
    this._setExpanded(true);
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.classList.add("open");
    this.details.open = true;

    if (!this._isModal) {
      if (this.focusIsTrapped) {
        window.removeTrapFocus(this.returnFocusEl);
        this.focusIsTrapped = false;
      }

      window.unlockScroll(this);
    }
  }

  _forceClosed() {
    if (this.details.open) this._dispatchClosing();
    this._setExpanded(false);
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.classList.remove("open");
    this.details.open = false;

    if (this.focusIsTrapped) {
      window.removeTrapFocus(this.returnFocusEl);
      this.focusIsTrapped = false;
    }

    window.unlockScroll(this);
  }

  open() {
    if (this.details.open) return;
    this._setExpanded(true);
    this.removeEventListener("transitionend", this.handleTransitionEnd);

    this.handleTransitionEnd = () => {
      this.removeEventListener("transitionend", this.handleTransitionEnd);
      this.details.style.setProperty("--filter-content-height", "auto");

      const targetListbox = this._targetListbox;
      if (targetListbox) {
        targetListbox.setAttribute("active", "");
      }
    };

    this.addEventListener("transitionend", this.handleTransitionEnd);

    this._keepPagePosition(() => {
      this.details.open = true;
    });
    this.dispatchEvent(
      new CustomEvent("filter-drawer:opening", {
        bubbles: true,
        detail: { modal: this._isModal },
      }),
    );

    if (this._isModal) {
      window.lockScroll(this);
      this.returnFocusEl = document.activeElement;
      window.trapFocus(this, this.querySelector("[data-close]"));
      this.focusIsTrapped = true;
    }

    window.requestAnimationFrame(() => {
      this.details.style.setProperty(
        "--filter-content-height",
        `${this.content.scrollHeight}px`,
      );
      window.requestAnimationFrame(() => {
        this.details.classList.add("open");
      });
    });
  }

  close() {
    if (!this.details.open) return;
    this._setExpanded(false);
    this._dispatchClosing();

    if (this.content) {
      this.removeEventListener("transitionend", this.handleTransitionEnd);

      this.handleTransitionEnd = () => {
        this.removeEventListener("transitionend", this.handleTransitionEnd);
        this.details.style.removeProperty("--filter-content-height");
        this._keepPagePosition(() => {
          this.details.open = false;
        });

        if (this.focusIsTrapped) {
          window.removeTrapFocus(this.returnFocusEl);
          this.focusIsTrapped = false;
        }

        window.unlockScroll(this);
      };

      this.addEventListener("transitionend", this.handleTransitionEnd);

      const targetListbox = this._targetListbox;
      if (targetListbox) {
        targetListbox.removeAttribute("active");
      }
      this.details.style.setProperty(
        "--filter-content-height",
        `${this.content.scrollHeight}px`,
      );

      window.requestAnimationFrame(() => {
        this.details.classList.remove("open");
      });
    } else {
      this.details.open = false;
      this.details.classList.remove("open");
    }
  }

  // Fired as the close starts, not after its transition, so a listener can
  // start work (e.g. fetching filtered results) while the drawer animates out.
  _dispatchClosing() {
    this.dispatchEvent(
      new CustomEvent("filter-drawer:closing", { bubbles: true }),
    );
  }

  /**
   * Opens or closes the in-flow (desktop) panel without moving the page under
   * it. While the sticky bar is stuck, the panel's height change would
   * otherwise shift everything below it by the same amount.
   */
  _keepPagePosition(toggle) {
    const sticky = this._stickyAncestor;
    const anchor = sticky?.nextElementSibling;

    if (this._isModal || !anchor || !this._isStuck(sticky)) {
      toggle();
      return;
    }

    const before = anchor.getBoundingClientRect().top;
    toggle();
    window.scrollBy(0, anchor.getBoundingClientRect().top - before);
  }

  _isStuck(sticky) {
    const stickyTop = parseFloat(getComputedStyle(sticky).top) || 0;
    return (
      window.scrollY > 0 && sticky.getBoundingClientRect().top <= stickyTop + 1
    );
  }

  get _stickyAncestor() {
    for (let el = this.parentElement; el; el = el.parentElement) {
      if (getComputedStyle(el).position === "sticky") return el;
    }
    return null;
  }

  _setExpanded(expanded) {
    this.summary.classList.toggle("is-active", expanded);
    this.triggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", String(expanded));
    });
  }

  /** Buttons outside the drawer that toggle it, linked by aria-controls. */
  get triggers() {
    if (!this.id) return [];

    return [
      ...document.querySelectorAll(
        `[data-filter-drawer-toggle][aria-controls="${this.id}"]`,
      ),
    ];
  }

  get _allowToggle() {
    return true;
  }

  get _isModal() {
    const isModalWhenQuery = this.getAttribute("is-modal-when");

    if (!isModalWhenQuery) return true;

    return window.matchMedia(isModalWhenQuery).matches;
  }

  get _targetListbox() {
    return document.getElementById(this.getAttribute("target-listbox"));
  }

  get details() {
    return this.querySelector("details");
  }

  get content() {
    return this.querySelector("[data-content]");
  }

  get summary() {
    return this.querySelector("summary");
  }

  get _closeOnChange() {
    const closeOnChangeWhenQuery =
      this.getAttribute("close-on-change-when") || null;

    if (!closeOnChangeWhenQuery) return false;

    return window.matchMedia(closeOnChangeWhenQuery).matches;
  }
}

if (!customElements.get("filter-drawer")) {
  customElements.define("filter-drawer", FilterDrawer);
}
